import { Grid, MenuItem, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';

import { Field } from 'formik';
import { compose } from 'recompose';
import { isEqual, values } from 'lodash';
import Hint from 'modules/common/hint.component';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { Checkbox, ChipSelect, Select, TextField } from 'modules/intake/form';
import { TemporaryLivingBenefits, TemporaryLivingInformation } from 'modules/intake/models';
import ConfirmationDialog from 'modules/common/confirmationDialog.component';
import ExpansionPanel from 'modules/common/expansionPanel.component';
import ExpansionPanelDetails from 'modules/common/expansionPanelDetails.component';
import ExpansionPanelSummary from 'modules/common/expansionPanelSummary.component';
import Log from 'utilities/log';

import styles from 'modules/intake/styles';

const UNSAVED_CONTACTS_MESSAGE = 'There are unsaved family members. Please save the intake form to add them.';
const UNSAVED_PETS_MESSAGE = 'There are unsaved pets. Please save the intake form to add them.';

class TemporaryLivingForm extends Component {
  state = {
    showConfirmation: false,
  };

  shouldComponentUpdate(nextProps, nextState) {
    // contacts and pets feed the occupant selectors, so changes to either
    // (new family member or pet, or a save assigning ids) must re-render
    // moveTypeId gates the Initiate Service toggle, so a move type change must re-render too
    return this.props.temporaryLiving !== nextProps.temporaryLiving ||
      this.props.moveTypeId !== nextProps.moveTypeId ||
      this.props.assignmentTypes !== nextProps.assignmentTypes ||
      this.props.contacts !== nextProps.contacts ||
      this.props.pets !== nextProps.pets ||
      !isEqual(this.state, nextState);
  }

  renderHeader() {
    const { moveTypeId, classes, temporaryLiving, handleChange, handleBlur } = this.props;

    return (
      <div className="row align-center">
        <Typography variant="h3">Temporary Living</Typography>
        <Field
          component={Checkbox}
          className={classes.headerToggle}
          name="temporaryLiving.isTemporaryLivingServiceInitiated"
          value={temporaryLiving.isTemporaryLivingServiceInitiated}
          onChange={(event) => {
            // this value is the current state, not what it will change to if we allow it
            if (event.target.value === 'checked') {
              this.setState({ showConfirmation: true });
            } else {
              if (!temporaryLiving.isTemporaryLivingServiceInitiated && (moveTypeId === 0 || moveTypeId === 7)){
                this.setState({ showChangeMoveType: true });
              } else {
                handleChange(event);
              }
            }
          }}
          onBlur={handleBlur}
          label="Initiate Service"
        />       
      </div>
    );

  }

  handleDisableService = () => {
    // need to remember old id so backend doesn't break
    const {
      temporaryLiving: { id },
    } = this.props;
    this.props.setFieldValue('temporaryLiving', new TemporaryLivingInformation({ id }), false);
    this.props.setFieldValue('benefits.temporaryLiving', new TemporaryLivingBenefits(), false);
    this.setState({ showConfirmation: false });
  };

  render() {
    Log.trace('RENDER', 'TemporaryLivingForm');
    const { temporaryLiving, contacts, pets, handleChange, handleBlur, assignmentTypes, classes } = this.props;
    const { showConfirmation, showChangeMoveType } = this.state;
    const activeAssignmentTypes = (assignmentTypes || []).filter((type) => type.isActive !== false && (type.title || type.description));

    // Newly added family members and pets have id 0 until the form autosaves.
    // The API links occupants by id, so hold the selector until the save lands.
    const savedContacts = values(contacts).filter((instance) => !!instance.id);
    const savedPets = values(pets).filter((instance) => !!instance.id);
    const hasUnsavedContacts = savedContacts.length !== values(contacts).length;
    const hasUnsavedPets = savedPets.length !== values(pets).length;

    return (
      <div className="form-group">
        <ExpansionPanel expanded={temporaryLiving.isTemporaryLivingServiceInitiated}>
          <ExpansionPanelSummary>{this.renderHeader()}</ExpansionPanelSummary>
          <ExpansionPanelDetails>
            <Grid container spacing={1}>
              <Grid item xs={12} md={6}>
                <Field
                  component={ChipSelect}
                  name="temporaryLiving.occupantContacts"
                  label="Family Members in Temp Living"
                  value={temporaryLiving.occupantContacts}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  disabled={hasUnsavedContacts}
                  helperText={hasUnsavedContacts ? UNSAVED_CONTACTS_MESSAGE : undefined}
                  labelAccessor={(value) => `${value.firstName} ${value.lastName}`}
                  labelWidth={200}
                  style={{ marginBottom: 5 }}
                >
                  {savedContacts.map((contact) => (
                    <MenuItem key={contact.id} value={contact}>
                      {`${contact.firstName} ${contact.lastName}`}
                    </MenuItem>
                  ))}
                </Field>
                <Hint>The Transferee is already included</Hint>
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  component={ChipSelect}
                  name="temporaryLiving.occupantPets"
                  label="Pets in Temp Living"
                  value={temporaryLiving.occupantPets}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  disabled={hasUnsavedPets}
                  helperText={hasUnsavedPets ? UNSAVED_PETS_MESSAGE : undefined}
                  labelAccessor={(value) => value.petName}
                  labelWidth={130}
                  style={{ marginBottom: 5 }}
                >
                  {savedPets.map((pet) => (
                    <MenuItem key={pet.id} value={pet}>
                      {pet.petName}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>
              <Grid item xs={12} md={9}>
                <div className="row mb-1">
                  <Field
                    component={TextField}
                    name="temporaryLiving.specialRequestsPreferences"
                    label="Special Requests/Preferences"
                    value={temporaryLiving.specialRequestsPreferences}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                  />
                </div>
                <div className="row mb-1">
                  <Field
                    component={Select}
                    name="temporaryLiving.assignmentTypeId"
                    label="Assignment Type"
                    className={classes.wideControl}
                    value={temporaryLiving.assignmentTypeId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    labelWidth={115}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {activeAssignmentTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>{type.title || type.description}</MenuItem>
                    ))}
                  </Field>
                  <Field
                    component={TextField}
                    name="temporaryLiving.bedroomsPreferred"
                    label="Bedroom Preference"
                    value={temporaryLiving.bedroomsPreferred}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    numeric
                  />
                </div>
              </Grid>
            </Grid>
          </ExpansionPanelDetails>
        </ExpansionPanel>
        <ConfirmationDialog
          open={showConfirmation}
          title="Disable Service?"
          confirmLabel="Yes"
          cancelLabel="No"
          onConfirm={this.handleDisableService}
          onCancel={() => this.setState({ showConfirmation: false })}
        >
          <Typography>
            If you disable this service, you will lose all data entered in this section and any timeline entries related to this service. <br /><br />Do you want to proceed?
          </Typography>
        </ConfirmationDialog>
        <ConfirmationDialog
          open={showChangeMoveType}
          title="Change Move Type?"
          confirmLabel="Change Move Type"
          cancelLabel="Cancel"
          onConfirm={() => {window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }); this.setState({ showChangeMoveType: false });}}
          onCancel={() => this.setState({ showChangeMoveType: false })}
        >
          <Typography>
            In order to initiate Destination Services, please change the move type from either Candidate or Intern.{' '}
            <br />
            <br />
            Do you want to proceed?
          </Typography>
        </ConfirmationDialog>
      </div>
    );
  }
}

TemporaryLivingForm.propTypes = {
  classes: PropTypes.object.isRequired,
  temporaryLiving: PropTypes.object.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  metadata: PropTypes.object.isRequired,
  contacts: PropTypes.array.isRequired,
  pets: PropTypes.array.isRequired,
  assignmentTypes: PropTypes.array,
  moveTypeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setFieldValue: PropTypes.func.isRequired,
};

export default compose(withStyles(styles))(TemporaryLivingForm);
